/**
 * Shared types for the ps-integrations Salesforce Config API contract.
 *
 * These mirror the handoff guide (salesforce-config-api.md). The mapping blobs
 * themselves are intentionally loose (`unknown` / `JsonValue`) because their
 * authoritative shape is whatever `GET` returns for a given subscriber — we
 * render editors off the live blob, not off a fixed schema.
 */

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

/** Consistent error envelope returned by every ps-integrations endpoint. */
export interface ApiError {
  error: string;
}

// ---------------------------------------------------------------------------
// Subscriber search (platform-service-main GraphQL, not ps-integrations)
// ---------------------------------------------------------------------------

/** One match from the subscriber-search autocomplete. */
export interface SubscriberSummary {
  id: string;
  name: string;
  ein: string | null;
}

/** Features ps-main records AI usage against. Matches `AiUsageFeature`. */
export const AI_USAGE_FEATURES = [
  "chat",
  "briefing",
  "structured",
  "voice_call",
  "sms",
] as const;

export type AiUsageFeature = (typeof AI_USAGE_FEATURES)[number];

/**
 * One row of ps-main's AI usage ledger (an Anthropic message or a Retell
 * call), flattened for the AI usage tab.
 *
 * Ids are raw UUIDs. `userId` is null when a public API key consumed the
 * usage. `employeeName` is null when the user has no employee record in the
 * subscriber.
 */
export interface AiUsageEvent {
  id: string;
  subscriberId: string;
  subscriberName: string;
  userId: string | null;
  userEmail: string | null;
  employeeName: string | null;
  provider: string;
  feature: string;
  model: string | null;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  costUsd: number;
  occurredAt: string;
}

/**
 * GET /api/ai-usage. `truncated` is true when the window held more events
 * than the server reads, so the totals undercount.
 */
export interface AiUsageResponse {
  events: AiUsageEvent[];
  since: string;
  truncated: boolean;
}

/**
 * One per-user Turso database the browser extension owns (ADR-26).
 *
 * GET /api/extension-stores. The listing carries no token.
 */
export interface ExtensionStoreSummary {
  userId: string;
  userEmail: string | null;
  subscriberId: string;
  name: string;
  consoleUrl: string;
  createdAt: string | null;
}

/**
 * Purposes ps-main accepts on `updateLlmPromptOverride`. Matches
 * `LLMPromptPurpose` in platform-service-main.
 */
export const LLM_PROMPT_PURPOSES = ["constituent_briefing"] as const;

export type LlmPromptPurpose = (typeof LLM_PROMPT_PURPOSES)[number];

export const LLM_PROMPT_PURPOSE_LABELS: Record<LlmPromptPurpose, string> = {
  constituent_briefing: "Constituent briefing",
};

export function isLlmPromptPurpose(value: string): value is LlmPromptPurpose {
  return (LLM_PROMPT_PURPOSES as readonly string[]).includes(value);
}

/** Product default from `subscribers.llmPrompt`. */
export interface LlmPrompt {
  prompt: string;
  purpose: LlmPromptPurpose;
}

/** The active override returned by `subscribers.updateLlmPromptOverride`. */
export interface LlmPromptOverride {
  id: string;
  prompt: string;
  purpose: LlmPromptPurpose;
  subscriberId: string;
}

/** GET /api/subscribers/:id/llm-prompt — override wins over the product default. */
export interface LlmPromptView {
  override: LlmPromptOverride | null;
  defaultPrompt: LlmPrompt | null;
}

/** GET /api/health */
export interface HealthResponse {
  ok: boolean;
  configured: boolean;
  assistantEnabled: boolean;
  /** Whether local dev-auth (pick a ps-main debug user) is active. */
  devAuthEnabled: boolean;
  /** The currently-selected dev user, or null if none has been picked yet. */
  devAuthUserId: string | null;
}

/** One entry from ps-main's `accounts.debugAuthentication.debugAccounts`. */
export interface DevAccount {
  id: string;
  email: string;
  isSuperuser: boolean;
}

// ---------------------------------------------------------------------------
// 1. Salesforce object fields
// ---------------------------------------------------------------------------

export enum FieldType {
  address = "address",
  base64 = "base64",
  boolean = "boolean",
  combobox = "combobox",
  currency = "currency",
  date = "date",
  datetime = "datetime",
  double = "double",
  email = "email",
  encryptedstring = "encryptedstring",
  id = "id",
  int = "int",
  location = "location",
  multipicklist = "multipicklist",
  percent = "percent",
  phone = "phone",
  picklist = "picklist",
  reference = "reference",
  string = "string",
  text = "text",
  textarea = "textarea",
  time = "time",
  url = "url",
}

export interface PicklistValue {
  label: string;
  value: string;
  active: boolean;
}

/** Salesforce "describe" metadata for a single field. */
export interface FieldMeta {
  name: string;
  label: string;
  type: FieldType;
  custom?: boolean;
  calculated?: boolean;
  autoNumber?: boolean;
  extraTypeInfo?: string | null;
  picklistValues?: PicklistValue[];
  /** Non-empty => this is a lookup/relationship field. */
  referenceTo?: string[];
  relationshipName?: string | null;
  createable?: boolean;
  updateable?: boolean;
  // Upstream may include additional keys; keep them addressable.
  [key: string]: JsonValue | undefined;
}

/** Response of the fields endpoint: keyed by field API name (incl. `Rel.Field`). */
export type FieldsResponse = Record<string, FieldMeta>;

export interface FieldsQuery {
  /** Include relationship-traversed fields keyed as `Relationship.Field`. */
  include_references?: boolean;
  /** Force a fresh describe against Salesforce (cache ~1 day otherwise). */
  refresh?: boolean;
}

// ---------------------------------------------------------------------------
// 2. Read-only SOQL
// ---------------------------------------------------------------------------

export interface SoqlRequest {
  /** A single SELECT statement; no `;`. */
  query: string;
  /** Optional, default 200, max 2000. */
  row_limit?: number;
}

export interface SoqlResponse {
  records: Array<Record<string, JsonValue>>;
  count: number;
}

export const SOQL_MAX_ROW_LIMIT = 2000;
export const SOQL_DEFAULT_ROW_LIMIT = 200;

// ---------------------------------------------------------------------------
// Subscriber integration status (incl. Salesforce instance URL)
// GET /api/v1/config/<subscriber_id>/
// ---------------------------------------------------------------------------

export type IntegrationStatusValue =
  | "connected"
  | "disconnected"
  | "error"
  | "pending";

export interface SalesforceIntegrationStatus {
  status: IntegrationStatusValue;
  export_enabled?: boolean | null;
  has_verified_filters?: boolean;
  resources_to_export?: string[];
  /** The subscriber's Salesforce instance URL, e.g. https://acme.my.salesforce.com. */
  instance_url?: string | null;
}

/** Shape of `GET /api/v1/config/<subscriber_id>/` (only the parts the UI uses). */
export interface IntegrationStatusesResponse {
  subscriber_integrations: {
    salesforce: SalesforceIntegrationStatus;
  } & Record<string, JsonValue>;
}

// ---------------------------------------------------------------------------
// Salesforce config (connection + import/export settings)
// GET/PUT /api/v1/config/<subscriber_id>/salesforce/
// ---------------------------------------------------------------------------

/**
 * One enabled subresource under `subresources_to_import` / `_export`.
 * The API accepts a bare string (coerced to `{ subresource, dry_run: true }`)
 * or an explicit object; Mechanicus always reads/writes the object form.
 */
export interface SalesforceConfiguredSubresource {
  subresource: string;
  dry_run: boolean;
}

/**
 * Per-resource import/export behavior on Salesforce credentials.
 * `constituents` and `interactions` are accepted; subresources may include
 * `assignees` (each with optional `dry_run`). Constituent flags may include
 * the clear-empty-assignee toggles.
 */
export interface SalesforceResourceConfiguration {
  resource_name: string;
  flags?: string[];
  subresources_to_import?: SalesforceConfiguredSubresource[];
  subresources_to_export?: SalesforceConfiguredSubresource[];
}

/**
 * The whole Salesforce config as returned by GET. The secret is never sent
 * down — `client_secret_set` tells you whether one is stored. `resources_to_*`
 * and the `import_*` flags are the writable settings the PUT preserves.
 */
export interface SalesforceConfig {
  auth_url: string | null;
  client_id: string | null;
  /** True when a client secret is stored (the secret itself is never returned). */
  client_secret_set?: boolean;
  export_to_sf_enabled?: boolean | null;
  resources_to_import?: string[] | null;
  resources_to_export?: string[] | null;
  resources_to_delete?: string[] | null;
  resource_configuration?: SalesforceResourceConfiguration[] | null;
  import_contact_social_links?: boolean | null;
  import_affiliation_social_links?: boolean | null;
  import_relationship_social_links?: boolean | null;
  import_interval_in_minutes?: number | null;
  last_import_started_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  [key: string]: JsonValue | undefined;
}

/**
 * The writable fields the PUT reads (WRITABLE_CREDENTIAL_FIELDS server-side).
 * PUT is read-modify-write: only keys you send change; the rest are preserved.
 * Omit `client_secret` (or send it blank) to keep the stored one.
 */
export interface SalesforceConfigInput {
  auth_url?: string;
  client_id?: string;
  client_secret?: string;
  export_to_sf_enabled?: boolean;
  resources_to_import?: string[];
  resources_to_export?: string[];
  resources_to_delete?: string[];
  resource_configuration?: SalesforceResourceConfiguration[];
  import_contact_social_links?: boolean;
  import_affiliation_social_links?: boolean;
  import_relationship_social_links?: boolean;
  import_interval_in_minutes?: number;
}

/**
 * POST /api/v1/config/<subscriber_id>/salesforce/interaction-assignee-import/
 * Enables interaction assignee import (mappings + subresource) and starts an
 * incremental import. At least one of `task` / `event` is required; each
 * object needs exactly one of primary field label or API name.
 */
export interface AssigneeFieldIdentifierInput {
  field_label?: string;
  field_name?: string;
}

export interface InteractionObjectAssigneeFieldInput {
  primary_assignee_field_label?: string;
  primary_assignee_field_name?: string;
  secondary_assignees?: AssigneeFieldIdentifierInput[];
}

export interface EnableInteractionAssigneeImportInput {
  task?: InteractionObjectAssigneeFieldInput;
  event?: InteractionObjectAssigneeFieldInput;
}

export interface ResolvedAssigneeField {
  field_label: string;
  field_name: string;
}

export interface InteractionObjectAssigneeFieldLabels {
  primary_assignee_field_label: string;
  primary_assignee_field_name: string;
  secondary_assignees?: ResolvedAssigneeField[];
}

export interface EnableInteractionAssigneeImportResult {
  task: InteractionObjectAssigneeFieldLabels | null;
  event: InteractionObjectAssigneeFieldLabels | null;
  workflow_id?: string;
  error?: string;
}

// ---------------------------------------------------------------------------
// Salesforce import filters
// GET  /api/v1/config/<subscriber_id>/salesforce/filters/saved/  → stored
// POST /api/v1/config/<subscriber_id>/salesforce/filters/        → save (+counts)
// POST /api/v1/config/<subscriber_id>/salesforce/filters/preview/→ counts only
// ---------------------------------------------------------------------------

/**
 * A single import filter. `filter_value` is optional (operators `known`/`empty`
 * take none) and may be a string or a list of strings (for `in` / `not in`).
 */
export interface SalesforceFilter {
  filter_field: string;
  filter_operator: string;
  filter_value?: string | string[] | null;
  source_object: string;
}

/** Live record counts the preview/save endpoints return (only Account/Contact). */
export interface FilterCounts {
  accounts?: number;
  contacts?: number;
}

/** Shape of `GET .../filters/saved/`. */
export interface SavedFiltersResponse {
  filters: SalesforceFilter[];
}

// ---------------------------------------------------------------------------
// 3. Mappings
// ---------------------------------------------------------------------------

export const MAPPING_TYPES = [
  "contact_mapping",
  "account_mapping",
  "main_opportunity_mapping",
  "campaign_mapping",
  "user_mapping",
  "interaction_mappings",
  "payment_mapping",
  "social_links_mappings",
] as const;

export type MappingType = (typeof MAPPING_TYPES)[number];

/** Human-readable descriptions for the mapping-type picker. */
export const MAPPING_TYPE_LABELS: Record<MappingType, string> = {
  contact_mapping: "SF Contact → Instil constituent",
  account_mapping: "SF Account → Instil constituent (incl. assignees)",
  main_opportunity_mapping: "SF Opportunity → Instil opportunity/contribution",
  campaign_mapping: "SF Campaign → Instil campaign",
  user_mapping: "SF user (PermissionSetAssignment) → Instil user",
  interaction_mappings:
    "SF Task and Event → Instil interaction (this one is a list)",
  payment_mapping: "SF payment → Instil transaction",
  social_links_mappings: "SF fields → social links (this one is a list)",
};

/** `interaction_mappings` and `social_links_mappings` are stored as an array. Every other type is an object. */
export function isListMapping(type: MappingType): boolean {
  return type === "interaction_mappings" || type === "social_links_mappings";
}

/** The universal "this Instil field comes from that SF field" reference. */
export interface SalesforceField {
  field_name: string;
  object_relation_name?: string | null;
  custom_object_relation_name?: string | null;
  is_required?: boolean;
}

/** A mapping blob — shape varies per type, so callers treat it as opaque JSON. */
export type MappingBlob = JsonValue;

/** Whether a fetched mapping is the subscriber's own or the system default. */
export type MappingSource = "subscriber" | "default";

/** Envelope returned by GET mapping: `{ data: { source, mapping } }`. */
export interface MappingEnvelope {
  data: {
    source: MappingSource;
    mapping: MappingBlob;
  };
}

/** Unwrapped result the client returns to UI code. */
export interface MappingResult {
  source: MappingSource;
  mapping: MappingBlob;
}

export function isMappingType(value: string): value is MappingType {
  return (MAPPING_TYPES as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Salesforce ↔ Instil custom field mappings
// GET/DELETE /api/v1/salesforce/<subscriber_id>/custom_field_mappings/
// Editability: PATCH ps-main worker
//   /internal/v1/subscribers/<subscriber_id>/custom-fields/<custom_field_id>
//   { "is_editable": boolean }
// ---------------------------------------------------------------------------

/** Instil custom-field definition types (ps-main CustomFieldDefinitionType). */
export type InstilCustomFieldType =
  | "text"
  | "textarea"
  | "select"
  | "multiselect"
  | "date"
  | "checkbox";

/**
 * One active SF ↔ Instil custom-field mapping. GET returns only rows where
 * `deleted_at` is null; soft-delete sets that timestamp.
 */
export interface SalesforceCustomFieldMapping {
  salesforce_custom_field_label: string;
  salesforce_custom_field_name: string;
  /** Salesforce describe type, e.g. string | picklist | boolean. */
  salesforce_custom_field_type: string;
  /** Salesforce object API name, e.g. Account | Contact | Opportunity. */
  salesforce_object_name: string;
  instil_custom_field_id: string;
  instil_custom_field_type: InstilCustomFieldType;
  is_editable_in_instil: boolean;
  subscriber_id: string;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

/**
 * How to identify the mapping to soft-delete. Exactly one of the two arms —
 * by Instil CF id, or by SF field name + object.
 */
export type DeleteCustomFieldMappingLookup =
  | { instilCustomFieldId: string }
  | {
      salesforceCustomFieldName: string;
      salesforceObjectName: string;
    };

/** Shape of a successful DELETE. */
export interface DeleteCustomFieldMappingResponse {
  success: true;
}

// ---------------------------------------------------------------------------
// Subscriber configuration copy (sandbox to production)
// ---------------------------------------------------------------------------

/**
 * Configuration that the copy reports but never writes.
 *
 * Each entry names a reason. A reader uses the reason to decide what to do. The
 * key is the name that `ConfigDocument.manual` counts, so the Config copy tab
 * shows the reason next to the count.
 */
export const MANUAL: Record<string, string> = {
  employees:
    "A new employee needs a user account. The invite sends mail to a real person. Create these by hand, in the order your onboarding asks for.",
  invites: "An invite sends mail to a real person.",
  roles:
    "A custom role grants permissions. Copy it by hand, and confirm each grant.",
  resourceGroups:
    "A resource group scopes every other resource. Its ids appear on the resources that belong to it. Copy it first, by hand, and then repeat the copy.",
  savedFilters:
    "A saved filter holds the ids of labels, stages, and custom fields. Those ids belong to the source subscriber. Recreate the filter by hand after the labels and the custom fields exist.",
  automations:
    "An automation holds the ids of its trigger and of its action targets.",
  emailTemplates: "An email template can refer to a campaign or to a label.",
  forms: "A form refers to a payment processor and to a membership level.",
  membership: "A membership level refers to a form and to a price.",
  publicApiKeys:
    "The secret of an api key exists only at the moment it is created. Issue a new key, and give it to the caller.",
  portalSettings:
    "The portal slug is unique across every subscriber. Choose a new one.",
};

/**
 * The only 2 Salesforce objects that
 * `map_salesforce_fields_to_instil_custom_fields` accepts. The command declares
 * `--contact_cf_list` and `--account_cf_list`, and no other list.
 */
const CUSTOM_FIELD_LIST_FLAGS: Record<string, string> = {
  contact: "contact_cf_list",
  account: "account_cf_list",
};

/**
 * Build the list arguments of `map_salesforce_fields_to_instil_custom_fields`
 * from the Salesforce custom field names of the source subscriber.
 *
 * `unsupported` names each Salesforce object that the command cannot take. The
 * caller reports those instead of writing an argument that the command refuses.
 */
export function customFieldListFlags(namesByObject: Record<string, string[]>): {
  flags: string[];
  unsupported: string[];
} {
  const flags: string[] = [];
  const unsupported: string[] = [];
  for (const [object, fields] of Object.entries(namesByObject)) {
    if (fields.length === 0) continue;
    const flag = CUSTOM_FIELD_LIST_FLAGS[object.toLowerCase()];
    if (flag) flags.push(`--${flag}=${fields.join(",")}`);
    else unsupported.push(object);
  }
  return { flags, unsupported };
}

/**
 * The resources that can hold locked fields. Matches
 * `LockedFieldResourceName` in platform-service-main.
 */
export const LOCKED_FIELD_RESOURCES = [
  "person",
  "organization",
  "social_link",
] as const;

export type LockedFieldResource = (typeof LOCKED_FIELD_RESOURCES)[number];

export const LOCKED_FIELD_RESOURCE_LABELS: Record<LockedFieldResource, string> =
  {
    person: "Person",
    organization: "Organization",
    social_link: "Social links",
  };

export function isLockedFieldResource(
  value: string,
): value is LockedFieldResource {
  return (LOCKED_FIELD_RESOURCES as readonly string[]).includes(value);
}

/** GET /api/subscribers/:id/locked-fields — the locked set, and what may be locked. */
export interface LockedFieldsView {
  resourceName: LockedFieldResource;
  lockedFields: string[];
  lockableFields: string[];
}

/** Confirmed editability after a worker-service PATCH. */
export interface SetCustomFieldEditableResponse {
  is_editable: boolean;
}
